import { ReactNode } from "react";
import { ClaudeMark, ExternalArrow, SMark, SPhoto, Tick } from "./icons";
import { PageCtx } from "./ctx";
import { asset, HELP_CENTER_URL, ROUTE_CARDS, STAT_COUNT } from "./data";
import PhoneFrame from "./PhoneFrame";
import { rich } from "./rich";

const FAQ_COUNT = 7;

/** Title bar of a mini chat: ChatGPT's three window dots, or Claude's spark. */
const MiniChatTop = ({ ctx }: { ctx: PageCtx }) => (
	<div className="mc-top">
		{ctx.assistant === "claude" ? (
			<ClaudeMark />
		) : (
			<>
				<span />
				<span />
				<span />
			</>
		)}
		{ctx.name}
	</div>
);

const Result = ({ children }: { children: ReactNode }) => (
	<div className="bt-result">
		<i>
			<Tick stroke="currentColor" />
		</i>
		{children}
	</div>
);

/** "The best of both, without copy-paste": four steps from chat idea to the trip in the app. */
export function Together({ ctx }: { ctx: PageCtx }) {
	const { tr } = ctx;
	return (
		<section className="together" id="story" aria-labelledby="atp-bt-h">
			<SPhoto clipId="atp-s-clip-2" photo={asset("photos/s-mountains.jpg")} className="s-a" />
			<div className="wrap">
				<div className="sec-head">
					<p className="eyebrow">{tr("together.eyebrow")}</p>
					<h2 className="h2" id="atp-bt-h">
						{tr("together.h2Pre")}
						<span className="hl">{tr("together.h2Hl")}</span>
					</h2>
					<p className="lede">{tr("together.lede")}</p>
				</div>
				<div className="bt-grid">
					<article className="bt-step">
						<div className="bt-vis">
							<div className="mc">
								<MiniChatTop ctx={ctx} />
								<div className="mc-u">{tr("together.s1.user")}</div>
								<div className="mc-a">{rich(tr("together.s1.ai"))}</div>
							</div>
						</div>
						<Result>{tr("together.s1.result")}</Result>
						<h3>
							<span className="bt-n">1</span>
							{tr("together.s1.title")}
						</h3>
						<p>{tr("together.s1.text")}</p>
					</article>
					<article className="bt-step">
						<div className="bt-vis">
							<div className="mc">
								<MiniChatTop ctx={ctx} />
								<div className="mc-u">{tr("demo.savePrompt")}</div>
								<div className="mc-tool">
									<span className="ic">
										<SMark fill="#13c892" />
									</span>
									{tr("together.s2.tool")}
								</div>
								<div className="mc-saved">
									<img src={asset("photos/thailand.jpg")} alt="" />
									<span>
										<b>{tr("demo.thailand.name")}</b>
										<small>{tr("together.s2.savedMeta")}</small>
									</span>
									<em>{tr("demo.openTrip")}</em>
								</div>
							</div>
						</div>
						<Result>{tr("together.s2.result")}</Result>
						<h3>
							<span className="bt-n">2</span>
							{tr("together.s2.title")}
						</h3>
						<p>{tr("together.s2.text")}</p>
					</article>
					<article className="bt-step">
						<div className="bt-vis">
							<div className="mc-u float">{tr("together.s3.user")}</div>
							<img className="bt-shot" src={asset("img/nd-day1-thailand.webp")} width={720} height={580} loading="lazy" alt={tr("together.s3.alt")} />
						</div>
						<Result>{tr("together.s3.result")}</Result>
						<h3>
							<span className="bt-n">3</span>
							{tr("together.s3.title")}
						</h3>
						<p>{tr("together.s3.text")}</p>
					</article>
					<article className="bt-step">
						<div className="bt-vis">
							<div className="bt-phone">
								<PhoneFrame img={{ src: asset("img/nd-route-thailand.webp"), loading: "lazy", alt: tr("together.s4.alt") }} />
							</div>
						</div>
						<Result>{tr("together.s4.result")}</Result>
						<h3>
							<span className="bt-n">4</span>
							{tr("together.s4.title")}
						</h3>
						<p>{tr("together.s4.text")}</p>
					</article>
				</div>
			</div>
		</section>
	);
}

/** "Over 2 million trips planned": the stats and the sixteen route cards, each opening the assistant with that route. */
export function Destinations({ ctx }: { ctx: PageCtx }) {
	const { tr, promptHref, trackCta } = ctx;
	return (
		<section className="dest" id="ideas" aria-labelledby="atp-dest-h">
			<SPhoto clipId="atp-s-clip-3" photo={asset("photos/thailand.jpg")} className="s-b" />
			<div className="wrap">
				<div className="dest-top">
					<div className="sec-head" style={{ margin: 0 }}>
						<p className="eyebrow">{tr("dest.eyebrow")}</p>
						<h2 className="h2" id="atp-dest-h">
							{tr("dest.h2Pre")}
							<span className="hl">{tr("dest.h2Hl")}</span>
						</h2>
						<p className="lede">{tr("dest.lede")}</p>
					</div>
					<div className="stats">
						{Array.from({ length: STAT_COUNT }, (_, i) => i + 1).map((n) => (
							<div className={`stat s${n}`} key={n}>
								<b>{tr(`stat${n}.value`)}</b>
								<span>{tr(`stat${n}.label`)}</span>
							</div>
						))}
					</div>
				</div>
				<div className="trips-row">
					{ROUTE_CARDS.map((card) => (
						<article className="tcard2" key={card.id}>
							<img src={asset(`img/card-${card.id}.webp`)} alt={tr(`route.${card.id}.alt`)} loading="lazy" />
							<img className="tc2-flag" src={asset(card.flag)} alt="" />
							<img className="tc2-photo" src={asset(card.photo)} alt="" loading="lazy" />
							<span className="tc2-meta">{tr(`route.${card.id}.meta`)}</span>
							<div className="tc2-foot">
								<b>{tr(`route.${card.id}.title`)}</b>
								<a
									className="gpt-link"
									href={promptHref(tr(`route.${card.id}.prompt`))}
									target="_blank"
									rel="noopener"
									data-where={`card-${card.id}`}
									onClick={() => trackCta(`card-${card.id}`, "open_assistant")}
								>
									{tr("route.go")}
									<ExternalArrow />
								</a>
							</div>
						</article>
					))}
				</div>
			</div>
		</section>
	);
}

/** The FAQ: the first question open, the rest closed. The same text feeds the page's FAQPage structured data. */
export function Faq({ ctx }: { ctx: PageCtx }) {
	const { tr, trA } = ctx;
	return (
		<section className="atp-faq" id="faq" aria-labelledby="atp-faq-h">
			<div className="wrap faq-grid">
				<div className="sec-head" style={{ margin: 0 }}>
					<p className="eyebrow">{tr("faq.eyebrow")}</p>
					<h2 className="h2" id="atp-faq-h">
						{tr("faq.h2")}
					</h2>
					<p className="lede">
						{tr("faq.ledePre")}
						<a href={HELP_CENTER_URL} style={{ color: "var(--green-ink)" }}>
							{tr("faq.ledeLink")}
						</a>
						{tr("faq.ledePost")}
					</p>
				</div>
				<div>
					{Array.from({ length: FAQ_COUNT }, (_, i) => i).map((i) => (
						<details className="qa" key={i} open={i === 0}>
							<summary>{trA(`faqItems.${i}.question`)}</summary>
							<p>{trA(`faqItems.${i}.answer`)}</p>
						</details>
					))}
				</div>
			</div>
		</section>
	);
}

/** Closing call to action on the dark band, with three phones peeking up from the bottom. */
export function FinalCta({ ctx }: { ctx: PageCtx }) {
	const { tr, trA, listingUrl, promptHref, trackCta } = ctx;
	return (
		<section className="cta" aria-labelledby="atp-cta-h">
			<svg className="smark" viewBox="0 0 81 108" fill="#13c892" aria-hidden="true">
				<use href="#atp-smark" />
			</svg>
			<div className="wrap cta-inner">
				<div className="cta-copy">
					<h2 id="atp-cta-h">{tr("cta.h2")}</h2>
					<p>{tr("cta.text")}</p>
					<div className="hero-ctas">
						<a
							className="btn btn-green"
							href={listingUrl}
							target="_blank"
							rel="noopener"
							data-where="cta-add"
							onClick={() => trackCta("cta-add", "add_connector")}
						>
							{trA("setup.oneClick")}
						</a>
						<a
							className="btn btn-white gpt-link"
							href={promptHref(tr("prompt.thailandJuly"))}
							target="_blank"
							rel="noopener"
							data-where="cta"
							onClick={() => trackCta("cta", "open_assistant")}
						>
							{tr("cta.try")}
							<ExternalArrow />
						</a>
					</div>
				</div>
				<div className="cta-phones" aria-hidden="true">
					<PhoneFrame img={{ src: asset("img/nd-days-japan.webp"), loading: "lazy" }} />
					<PhoneFrame img={{ src: asset("img/nd-route-thailand.webp"), loading: "lazy" }} />
					<PhoneFrame img={{ src: asset("img/nd-days-italy.webp"), loading: "lazy" }} />
				</div>
			</div>
		</section>
	);
}
